// ./src/components/features/VolunteerCheckin/VolunteerCheckinList/VolunteerCheckinList.tsx
//
// The day-of volunteer roster: one row per assigned crew member (person × slot) with
// their role, shift window, live attendance status, and a one-tap Check-in / Check-out
// button. Responsive — a Table on desktop, cards below. Check-in/out times are stamped
// server-side (see VolunteerAssignmentService.attendance); this view just reflects them.

import React from "react";
import { Paper, Stack, Group, Text, Box, Badge, ThemeIcon, ActionIcon, Skeleton, Table, Button, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VolunteerAssignment, VolunteerAttendanceAction, VOLUNTEER_ATTENDANCE } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import { AppIcon, EmptyState, ResponsiveMenu, SwipeableCard, SwipeAction, useSwipeableList, CardSkeletonActions, CardSkeletonLines } from "@/components/generic";
import type { ActionSheetItem } from "@/components/generic";
import { formatEventDateTime, formatEventWhenParts, HIGHLIGHT_ROW_BG, recordHasRight } from "@/utils";

// ============================================
// Row model + status helpers
// ============================================

export type CheckinStatus = "expected" | "onsite" | "left" | "noshow";

export interface CheckinRow {
  assignment: VolunteerAssignment;
  guestName: string;
  slotName: string;
  roleName: string | null;
  roleColor?: string | null;
  roleIcon?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  status: CheckinStatus;
}

export const checkinStatusOf = (a: VolunteerAssignment): CheckinStatus => {
  if (a.noShow) {
    return "noshow";
  }
  if (a.checkedOutAt) {
    return "left";
  }
  if (a.checkedInAt) {
    return "onsite";
  }
  return "expected";
};

/** Human duration between check-in and check-out (null unless both are set). */
export const durationLabel = (a: VolunteerAssignment): string | null => {
  if (!a.checkedInAt || !a.checkedOutAt) {
    return null;
  }
  const mins = Math.max(0, Math.round((new Date(a.checkedOutAt).getTime() - new Date(a.checkedInAt).getTime()) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// A volunteer who checked in this many minutes BEFORE the shift start, or checked out this
// many minutes AFTER the shift end, is flagged — a potential timing issue to look at
// (arrived early / ran over). Tune here (could later be a per-event setting).
export const SHIFT_EARLY_START_WARN_MIN = 15;
export const SHIFT_LATE_END_WARN_MIN = 15;

const minsBetween = (fromIso?: string | null, toIso?: string | null): number | null => {
  if (!fromIso || !toIso) {
    return null;
  }
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return null;
  }
  return Math.round((to - from) / 60000);
};

interface TimingWarn {
  earlyStartMin: number; // minutes checked in before the shift start, when past the threshold
  lateEndMin: number; // minutes checked out after the shift end, when past the threshold
}

const timingWarn = (row: CheckinRow): TimingWarn => {
  const a = row.assignment;
  const early = minsBetween(a.checkedInAt, row.startsAt); // start − checkedIn (positive = came early)
  const late = minsBetween(row.endsAt, a.checkedOutAt); // checkedOut − end (positive = ran over)

  return {
    earlyStartMin: early != null && early > SHIFT_EARLY_START_WARN_MIN ? early : 0,
    lateEndMin: late != null && late > SHIFT_LATE_END_WARN_MIN ? late : 0,
  };
};

const STATUS_META: Record<CheckinStatus, { color: string; icon: string; key: string; fallback: string }> = {
  expected: { color: "gray", icon: "Clock", key: "volunteers:checkin.status.expected", fallback: "Expected" },
  onsite: { color: "teal", icon: "UserCheck", key: "volunteers:checkin.status.onsite", fallback: "On-site" },
  left: { color: "blue", icon: "Logout", key: "volunteers:checkin.status.left", fallback: "Left" },
  noshow: { color: "red", icon: "UserX", key: "volunteers:checkin.status.noshow", fallback: "No-show" },
};

// Stored UTC → shown in the EVENT's timezone (falls back to the viewer's zone).
const timeOnly = (iso: string | null | undefined, tz?: string | null): string => (iso ? formatEventDateTime(iso, tz) : "—");

// ============================================
// Props
// ============================================

export interface VolunteerCheckinListProps {
  rows: CheckinRow[];
  onAction: (assignmentId: string, action: VolunteerAttendanceAction) => void;
  /** Open the manual check-in/out time editor for a row. */
  onEditTimes?: (row: CheckinRow) => void;
  busyId?: string | null;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  /** IANA timezone of the event — shift and check-in/out times are shown in this zone. */
  eventTimezone?: string | null;
  hasQueryNarrowing?: boolean;
  /** When false, the roster is view-only (no check-in/out buttons). */
  canAct?: boolean;
}

// ============================================
// Sub-components
// ============================================

const StatusChip: React.FC<{ status: CheckinStatus; size?: "sm" | "md" }> = ({ status, size = "md" }) => {
  const { t } = useTranslation(["volunteers"]);
  const meta = STATUS_META[status];

  return (
    <Badge size={size} variant={status === "onsite" || status === "noshow" ? "filled" : "light"} color={meta.color} leftSection={<AppIcon iconName={meta.icon} size={12} />}>
      {t(meta.key, { defaultValue: meta.fallback })}
    </Badge>
  );
};

// A scannable amber badge when a volunteer's actual times fall outside the planned shift
// window (arrived early / ran over). The tooltip spells out how far off, so a coordinator
// can spot and address timing issues.
const TimingWarnBadge: React.FC<{ warn: TimingWarn }> = ({ warn }) => {
  const { t } = useTranslation(["volunteers"]);

  if (!warn.earlyStartMin && !warn.lateEndMin) {
    return null;
  }

  const parts: string[] = [];
  if (warn.earlyStartMin) {
    parts.push(t("volunteers:checkin.timing.early", { defaultValue: "Checked in {{count}} min before the shift start", count: warn.earlyStartMin }));
  }
  if (warn.lateEndMin) {
    parts.push(t("volunteers:checkin.timing.late", { defaultValue: "Checked out {{count}} min after the shift end", count: warn.lateEndMin }));
  }

  const label =
    warn.earlyStartMin && warn.lateEndMin
      ? t("volunteers:checkin.timing.both", { defaultValue: "Off schedule" })
      : warn.earlyStartMin
        ? t("volunteers:checkin.timing.earlyShort", { defaultValue: "Early start" })
        : t("volunteers:checkin.timing.lateShort", { defaultValue: "Ran over" });

  return (
    <Tooltip label={parts.join(" · ")} withArrow multiline w={260} position="top">
      <Badge size="sm" variant="light" color="orange" style={{ flex: "0 0 auto" }} leftSection={<AppIcon iconName="AlertTriangle" size={11} />}>
        {label}
      </Badge>
    </Tooltip>
  );
};

// Shift as a compact two-line date/time (event timezone) so a long range never truncates.
const ShiftWhen: React.FC<{ row: CheckinRow; tz?: string | null }> = ({ row, tz }) => {
  const w = formatEventWhenParts(row.startsAt, row.endsAt, tz);

  if (!w) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <>
      <Text size="sm" c="dimmed" lineClamp={1}>
        {w.date}
      </Text>
      <Text size="sm" c="dimmed" lineClamp={1}>
        {w.time}
      </Text>
    </>
  );
};

// Primary action + a "more" menu, driven by the current status.
const RowActions: React.FC<{
  row: CheckinRow;
  onAction: (assignmentId: string, action: VolunteerAttendanceAction) => void;
  onEditTimes?: (row: CheckinRow) => void;
  busy: boolean;
  /** Hide the "more" (⋯) menu — on phones its items live in the swipe drawer instead. */
  showMenu?: boolean;
}> = ({ row, onAction, onEditTimes, busy, showMenu = true }) => {
  const { t } = useTranslation(["volunteers", "common"]);
  const id = row.assignment.id;

  if (!id) {
    return null;
  }

  const act = (action: VolunteerAttendanceAction) => onAction(id, action);

  const moreItems: ActionSheetItem[] = [];

  // ⚠ EDITING TIMES IS ROSTER WORK, NOT DOOR WORK, and since 2026-09-22 the two are different
  // permissions: `checkin` crew may mark somebody arrived or gone, `event.operate` crew may rewrite
  // the record afterwards. The server enforces that on `eventVolunteerAssignmentUpdate`, so without
  // this gate a door volunteer would see "Edit times", fill the drawer in, and be refused on save.
  // `recordHasRight` is fail-open, so an older API build that sends no rights behaves as before.
  if (onEditTimes && recordHasRight(row.assignment, "edit")) {
    moreItems.push({ label: t("volunteers:checkin.actions.editTimes", { defaultValue: "Edit times" }), iconName: "Clock", onClick: () => onEditTimes(row) });
  }

  if (row.status === "onsite" || row.status === "left") {
    moreItems.push({ label: t("volunteers:checkin.actions.undoCheckIn", { defaultValue: "Undo check-in" }), iconName: "ArrowBackUp", onClick: () => act(VOLUNTEER_ATTENDANCE.UNDO_CHECK_IN) });
  }
  if (row.status === "expected") {
    moreItems.push({ label: t("volunteers:checkin.actions.noShow", { defaultValue: "Mark no-show" }), iconName: "UserX", color: "red", onClick: () => act(VOLUNTEER_ATTENDANCE.NO_SHOW) });
  }

  const primary = (() => {
    switch (row.status) {
      case "expected":
        return (
          <Button size="md" variant="filled" color="teal" leftSection={<AppIcon iconName="UserCheck" size={16} />} onClick={() => act(VOLUNTEER_ATTENDANCE.CHECK_IN)} loading={busy} disabled={busy}>
            {t("volunteers:checkin.actions.checkIn", { defaultValue: "Check in" })}
          </Button>
        );
      case "onsite":
        return (
          <Button size="md" variant="light" color="blue" leftSection={<AppIcon iconName="Logout" size={16} />} onClick={() => act(VOLUNTEER_ATTENDANCE.CHECK_OUT)} loading={busy} disabled={busy}>
            {t("volunteers:checkin.actions.checkOut", { defaultValue: "Check out" })}
          </Button>
        );
      case "left":
        return (
          <Button size="md" variant="subtle" color="blue" leftSection={<AppIcon iconName="ArrowBackUp" size={16} />} onClick={() => act(VOLUNTEER_ATTENDANCE.UNDO_CHECK_OUT)} loading={busy} disabled={busy}>
            {t("volunteers:checkin.actions.reCheckIn", { defaultValue: "Back on-site" })}
          </Button>
        );
      case "noshow":
        return (
          <Button size="md" variant="subtle" color="gray" leftSection={<AppIcon iconName="ArrowBackUp" size={16} />} onClick={() => act(VOLUNTEER_ATTENDANCE.CLEAR_NO_SHOW)} loading={busy} disabled={busy}>
            {t("volunteers:checkin.actions.clearNoShow", { defaultValue: "Undo no-show" })}
          </Button>
        );
      default:
        return null;
    }
  })();

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      {primary}
      {showMenu && moreItems.length > 0 && (
        <ResponsiveMenu
          title={row.guestName || t("common:buttons.more", { defaultValue: "More" })}
          target={
            <ActionIcon variant="subtle" color="gray" size="md" disabled={busy} aria-label={t("common:buttons.more", { defaultValue: "More" })}>
              <AppIcon iconName="Dots" size={16} />
            </ActionIcon>
          }
          sections={[{ items: moreItems }]}
          menuProps={{ withArrow: true }}
        />
      )}
    </Group>
  );
};

// ============================================
// Main
// ============================================

const VolunteerCheckinListComponent: React.FC<VolunteerCheckinListProps> = ({ rows, onAction, onEditTimes, busyId, isLoaded, eventTimezone, hasQueryNarrowing, canAct = true }) => {
  const { t } = useTranslation(["volunteers", "common"]);
  const { isDesktop } = useAppEnvironment();
  const { openCard, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleSwipeStateChange = React.useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // Phone swipe actions = the secondary actions (edit times + status-appropriate undo /
  // no-show) that live in the ⋯ menu on wider screens. The primary Check-in/out button
  // stays a visible tap target on the card.
  const swipeActionsFor = React.useCallback(
    (row: CheckinRow): SwipeAction[] => {
      const id = row.assignment.id;
      if (!canAct || !id) {
        return [];
      }

      const busy = busyId === id;
      const acts: SwipeAction[] = [];

      // ⚠ The swipe actions MIRROR the ⋯ menu, rights gating included — a control hidden in one place
      // and reachable in the other is the same bug with an extra step.
      if (onEditTimes && recordHasRight(row.assignment, "edit")) {
        acts.push({ key: "edit", icon: "Clock", label: t("volunteers:checkin.actions.editTimes", { defaultValue: "Edit times" }), color: "blue", onClick: () => onEditTimes(row), disabled: busy });
      }
      if (row.status === "onsite" || row.status === "left") {
        acts.push({ key: "undoIn", icon: "ArrowBackUp", label: t("volunteers:checkin.actions.undoCheckIn", { defaultValue: "Undo check-in" }), color: "gray", onClick: () => onAction(id, VOLUNTEER_ATTENDANCE.UNDO_CHECK_IN), disabled: busy, secondary: true });
      }
      if (row.status === "left") {
        acts.push({ key: "undoOut", icon: "ArrowBackUp", label: t("volunteers:checkin.actions.undoCheckOut", { defaultValue: "Undo check-out" }), color: "gray", onClick: () => onAction(id, VOLUNTEER_ATTENDANCE.UNDO_CHECK_OUT), disabled: busy, secondary: true });
      }
      if (row.status === "expected") {
        acts.push({ key: "noshow", icon: "UserX", label: t("volunteers:checkin.actions.noShow", { defaultValue: "Mark no-show" }), color: "red", onClick: () => onAction(id, VOLUNTEER_ATTENDANCE.NO_SHOW), disabled: busy, secondary: true });
      }

      return acts;
    },
    [canAct, busyId, onEditTimes, onAction, t]
  );

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && rows.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={
            hasQueryNarrowing
              ? t("volunteers:checkin.empty.noMatchTitle", { defaultValue: "No volunteers match" })
              : t("volunteers:checkin.empty.title", { defaultValue: "No crew assigned yet" })
          }
          description={
            hasQueryNarrowing
              ? t("volunteers:checkin.empty.noMatchDescription", { defaultValue: "Try a different search or clear the filters." })
              : t("volunteers:checkin.empty.description", { defaultValue: "Assign crew to volunteer slots first — they'll appear here to check in." })
          }
          iconName={hasQueryNarrowing ? "Search" : "Users"}
          size="md"
        />
      </Paper>
    );
  }

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        <Table verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("volunteers:checkin.columns.name", { defaultValue: "Volunteer" })}</Table.Th>
              <Table.Th style={{ width: 200 }}>{t("volunteers:checkin.columns.role", { defaultValue: "Role & shift" })}</Table.Th>
              <Table.Th style={{ width: 220 }}>{t("volunteers:checkin.columns.times", { defaultValue: "In / out" })}</Table.Th>
              <Table.Th style={{ width: 120 }}>{t("volunteers:checkin.columns.status", { defaultValue: "Status" })}</Table.Th>
              <Table.Th style={{ width: 210 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {showSkeleton
              ? Array.from({ length: 8 }).map((_, i) => (
                  <Table.Tr key={i}>
                    <Table.Td><Skeleton height={14} width="45%" maw={200} /></Table.Td>
                    <Table.Td style={{ width: 200 }}><Skeleton height={14} width={140} /></Table.Td>
                    <Table.Td style={{ width: 220 }}><Skeleton height={14} width={160} /></Table.Td>
                    <Table.Td style={{ width: 120 }}><Skeleton height={18} width={80} radius="xl" /></Table.Td>
                    <Table.Td style={{ width: 210 }}><Skeleton height={28} width={120} radius="sm" /></Table.Td>
                  </Table.Tr>
                ))
              : rows.map((row) => {
                  const busy = busyId === row.assignment.id;
                  const dur = durationLabel(row.assignment);
                  const warn = timingWarn(row);
                  return (
                    <Table.Tr key={row.assignment.id} style={{ backgroundColor: busy ? HIGHLIGHT_ROW_BG : undefined, transition: "background-color 150ms ease" }}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text size="md" fw={600} lineClamp={1}>
                            {row.guestName}
                          </Text>
                          <TimingWarnBadge warn={warn} />
                        </Group>
                        <Text size="sm" c="dimmed" lineClamp={1}>
                          {row.slotName}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ width: 200 }}>
                        <Group gap={6} wrap="nowrap">
                          {row.roleName ? (
                            <Badge size="sm" variant="light" color={row.roleColor || "grape"}>
                              {row.roleName}
                            </Badge>
                          ) : null}
                        </Group>
                        <Box mt={2}>
                          <ShiftWhen row={row} tz={eventTimezone} />
                        </Box>
                      </Table.Td>
                      <Table.Td style={{ width: 220 }}>
                        <Text size="sm" c="dimmed">
                          {t("volunteers:checkin.inLabel", { defaultValue: "In" })}: {timeOnly(row.assignment.checkedInAt, eventTimezone)}
                          {/* #169 — where they reported (the desk the scan was made at). */}
                          {row.assignment.checkedInServicePoint?.name ? ` · ${row.assignment.checkedInServicePoint.name}` : ""}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {t("volunteers:checkin.outLabel", { defaultValue: "Out" })}: {timeOnly(row.assignment.checkedOutAt, eventTimezone)}
                          {row.assignment.checkedOutServicePoint?.name ? ` · ${row.assignment.checkedOutServicePoint.name}` : ""}
                        </Text>
                        {dur ? (
                          <Text size="sm" c="dimmed">
                            {t("volunteers:checkin.durationLabel", { defaultValue: "Duration" })}: {dur}
                          </Text>
                        ) : null}
                      </Table.Td>
                      <Table.Td style={{ width: 120 }}>
                        <StatusChip status={row.status} />
                      </Table.Td>
                      <Table.Td style={{ width: 210 }}>
                        {canAct ? <RowActions row={row} onAction={onAction} onEditTimes={onEditTimes} busy={busy} /> : null}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
          </Table.Tbody>
        </Table>
      </Paper>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <Paper key={i} p="md" withBorder radius="md">
            <Group gap="md" wrap="nowrap" align="flex-start">
              <Skeleton circle height={40} width={40} />
              <Box style={{ flex: 1, minWidth: 0 }}>
                {/* A volunteer ON SITE — the middle of a shift's three states (ROADMAP #127): name + status, role · slot, the
                    date and time, and the "In: …" line. Not yet arrived is one row shorter; gone home adds the Out time
                    (two lines on a phone) and the duration. The console is used during the shift, when they are mixed. */}
                <CardSkeletonLines count={5} sizes={['md', 'sm', 'sm', 'sm', 'sm']} gap={0} />
                {/* The primary step is a text button on EVERY width (a check-in console); only the ⋯ is tablet-only. */}
                {canAct && (
                  <Group gap={4} wrap="nowrap" justify="flex-end" mt="sm">
                    <Skeleton height={42} width={120} radius="sm" />
                    <CardSkeletonActions count={1} />
                  </Group>
                )}
              </Box>
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {rows.map((row) => {
        const busy = busyId === row.assignment.id;
        const dur = durationLabel(row.assignment);
        const warn = timingWarn(row);
        const id = row.assignment.id || "";
        const swipeActions = swipeActionsFor(row);

        const cardBody = (showMenu: boolean) => (
          <Paper p="md" withBorder radius="md" style={{ backgroundColor: busy ? HIGHLIGHT_ROW_BG : undefined }}>
            <Group gap="md" wrap="nowrap" align="flex-start">
              <ThemeIcon variant="light" color={row.roleColor || "grape"} radius="xl" size={40}>
                <AppIcon iconName={row.roleIcon || "Users"} size={22} />
              </ThemeIcon>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Group gap={6} wrap="nowrap" justify="space-between">
                  <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text size="md" fw={600} lineClamp={1}>
                      {row.guestName}
                    </Text>
                    <TimingWarnBadge warn={warn} />
                  </Group>
                  <StatusChip status={row.status} size="sm" />
                </Group>
                <Text size="sm" c="dimmed" lineClamp={1}>
                  {row.roleName ? `${row.roleName} · ` : ""}
                  {row.slotName}
                </Text>
                <ShiftWhen row={row} tz={eventTimezone} />
                {(row.assignment.checkedInAt || row.assignment.checkedOutAt) && (
                  <>
                    <Text size="sm" c="dimmed" mt={2}>
                      {t("volunteers:checkin.inLabel", { defaultValue: "In" })}: {timeOnly(row.assignment.checkedInAt, eventTimezone)}
                      {row.assignment.checkedInServicePoint?.name ? ` (${row.assignment.checkedInServicePoint.name})` : ""}
                      {row.assignment.checkedOutAt ? ` · ${t("volunteers:checkin.outLabel", { defaultValue: "Out" })}: ${timeOnly(row.assignment.checkedOutAt, eventTimezone)}` : ""}
                    </Text>
                    {dur ? (
                      <Text size="sm" c="dimmed">
                        {t("volunteers:checkin.durationLabel", { defaultValue: "Duration" })}: {dur}
                      </Text>
                    ) : null}
                  </>
                )}
                {canAct && (
                  <Box mt="sm">
                    <RowActions row={row} onAction={onAction} onEditTimes={onEditTimes} busy={busy} showMenu={showMenu} />
                  </Box>
                )}
              </Box>
            </Group>
          </Paper>
        );

        return (
          <React.Fragment key={id}>
            {/* Phones: swipe reveals the secondary actions (edit times / undo); the ⋯ menu is hidden. */}
            <Box hiddenFrom="sm">
              {swipeActions.length > 0 ? (
                <SwipeableCard
                  leftSwipeActions={swipeActions}
                  forceClose={shouldForceClose(id)}
                  onForceCloseComplete={() => handleForceCloseComplete(id)}
                  onSwipeStateChange={(isOpen) => handleSwipeStateChange(id, isOpen)}
                  disabled={busy}
                  borderRadius="var(--mantine-radius-md)"
                >
                  {cardBody(false)}
                </SwipeableCard>
              ) : (
                cardBody(false)
              )}
            </Box>
            {/* Tablet+: full ⋯ menu, no swipe. */}
            <Box visibleFrom="sm">{cardBody(true)}</Box>
          </React.Fragment>
        );
      })}
    </Stack>
  );
};

export const VolunteerCheckinList = React.memo(VolunteerCheckinListComponent);
